import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useMergeCustomers } from '../hooks/use-customer-mutations';
import { customerName } from '../schemas/customer-rules';

import { CustomerPicker } from './customer-picker';

type Keep = 'this' | 'other';

export interface MergeCustomerDialogProps {
  storeId: string;
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the record that survived, which is the other one when staff kept its profile. */
  onMerged: (survivor: Customer) => void;
}

const label = (customer: Customer) => customerName(customer) ?? customer.email;

/**
 * Folds a duplicate record into one: the kept customer's profile wins, the other's orders,
 * addresses, gift cards, tags and notes move over, and the other record is disabled and hidden.
 */
export function MergeCustomerDialog({
  storeId,
  customer,
  open,
  onOpenChange,
  onMerged,
}: MergeCustomerDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const merge = useMergeCustomers(storeId);
  const [other, setOther] = useState<Customer | null>(null);
  const [keep, setKeep] = useState<Keep>('this');
  const [pickError, setPickError] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    merge.reset();
  };

  const submit = async () => {
    if (!other) {
      setPickError(t('customers:merge.pickFirst'));
      return;
    }
    const [target, source] = keep === 'this' ? [customer, other] : [other, customer];
    try {
      const survivor = await merge.mutateAsync({ targetId: target.id, sourceId: source.id });
      close();
      onMerged(survivor);
    } catch (error) {
      setPickError(
        error instanceof ApiError && error.fieldErrors['body.sourceCustomerId']
          ? t('customers:merge.sourceGone')
          : message(error),
      );
    }
  };

  const kept = keep === 'this' ? customer : other;
  const removed = keep === 'this' ? other : customer;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('customers:merge.title')}</DialogTitle>
          <DialogDescription>{t('customers:merge.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="merge-customer-picker">{t('customers:merge.other')}</Label>
          <CustomerPicker
            id="merge-customer-picker"
            storeId={storeId}
            value={other?.id ?? null}
            exclude={[customer.id]}
            placeholder={t('customers:merge.otherPlaceholder')}
            aria-invalid={pickError !== null && other === null}
            onChange={(picked) => {
              setOther(picked);
              setPickError(null);
            }}
          />
        </div>
        {other ? (
          <RadioGroup
            value={keep}
            onValueChange={(value) => setKeep(value as Keep)}
            aria-label={t('customers:merge.keepLabel')}
            className="gap-2"
          >
            {(['this', 'other'] as const).map((option) => (
              <Label
                key={option}
                htmlFor={`merge-keep-${option}`}
                className="flex cursor-pointer items-start gap-2 rounded-md border p-3 font-normal"
              >
                <RadioGroupItem id={`merge-keep-${option}`} value={option} className="mt-0.5" />
                <span className="text-sm">
                  {t('customers:merge.keep', {
                    name: label(option === 'this' ? customer : other),
                    email: (option === 'this' ? customer : other).email,
                  })}
                </span>
              </Label>
            ))}
          </RadioGroup>
        ) : null}
        {kept && removed ? (
          <p className="text-muted-foreground text-sm" data-testid="merge-summary">
            {t('customers:merge.summary', { kept: kept.email, removed: removed.email })}
          </p>
        ) : null}
        {pickError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{pickError}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" disabled={merge.isPending} onClick={() => void submit()}>
            {t('customers:merge.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
